<script setup>
import {
  loadSubscriptionManagement,
  mutateSubscription,
  requestSubscriptionRenewal,
  syncSubscriptionPayment,
} from 'dashboard/api/subscriptionRenewal';
import {
  localizedPlanName,
  platformIntlLocale,
  platformTextDirection,
} from 'dashboard/helper/platformLocale';
import { safeRenewalCheckoutURL } from 'dashboard/helper/platformPolicyRules';
import SettingsLayout from '../SettingsLayout.vue';
import { computed, onBeforeUnmount, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';

const INVOICE_PAGE_SIZE = 10;
const PLAN_PAGE_SIZE = 8;
const PLAN_QUERY_MAX_LENGTH = 80;
const PLAN_SEARCH_DEBOUNCE_MS = 300;
const PLAN_SEARCH_MAX_WAIT_MS = 1000;
const PAYMENT_POLL_INTERVAL_MS = 2500;
const PAYMENT_POLL_MAX_ATTEMPTS = 8;
const EMPTY_PLAN_PAGE = {
  records: [],
  page: 1,
  page_size: PLAN_PAGE_SIZE,
  total: 0,
};
const PROVIDER_KEYS = {
  salla: 'SALLA',
  zid: 'ZID',
  shopify: 'SHOPIFY',
};
const STATUS_KEYS = {
  active: 'ACTIVE',
  trialing: 'TRIALING',
  pending: 'PENDING',
  past_due: 'PAST_DUE',
  expired: 'EXPIRED',
  canceled: 'CANCELED',
  suspended: 'SUSPENDED',
  paid: 'PAID',
  failed: 'FAILED',
  creating: 'PROCESSING',
  provider_result_ready: 'PROCESSING',
  checkout_failed: 'FAILED',
  processing: 'PROCESSING',
  checkout_created: 'PROCESSING',
  indeterminate: 'PROCESSING',
  manual_reconcile: 'PROCESSING',
};
const ACTION_REASON_KEYS = {
  free_plan: 'FREE_PLAN',
  internally_managed: 'INTERNALLY_MANAGED',
  automatic_renewal: 'AUTOMATIC_RENEWAL',
  marketplace_managed: 'MARKETPLACE_MANAGED',
  administratively_held: 'ADMINISTRATIVELY_HELD',
  payment_setup_required: 'PAYMENT_SETUP_REQUIRED',
  payment_gateway_unavailable: 'PAYMENT_GATEWAY_UNAVAILABLE',
};

const route = useRoute();
const router = useRouter();
const { t, locale } = useI18n();
const summary = ref(null);
const loading = ref(true);
const refreshing = ref(false);
const loadError = ref(false);
const mutationError = ref('');
const mutationLoading = ref('');
const loadedSummaryContext = ref('');
const planSearchInput = ref('');
const paymentVerificationState = ref('');
const paymentVerificationInvoiceId = ref('');
let summaryRequestGeneration = 0;
let mutationRequestGeneration = 0;
let planSearchDebounceTimer;
let planSearchMaxTimer;
let paymentPollTimer;
let paymentPollAttempts = 0;
let automaticPaymentSyncKey = '';

const normalizedPlanQuery = value =>
  Array.from(String(Array.isArray(value) ? value[0] || '' : value || '').trim())
    .slice(0, PLAN_QUERY_MAX_LENGTH)
    .join('');

const singleQueryValue = value => {
  if (Array.isArray(value)) return value.length === 1 ? value[0] : '';
  return value || '';
};

const normalizedPaymentId = value => {
  const paymentId = String(singleQueryValue(value)).trim();
  return /^[a-zA-Z0-9_-]{1,128}$/.test(paymentId) ? paymentId : '';
};

const normalizedInvoiceId = value => {
  const invoiceId = String(singleQueryValue(value)).trim();
  return /^inv_[a-zA-Z0-9_-]{1,92}$/.test(invoiceId) ? invoiceId : '';
};

planSearchInput.value = normalizedPlanQuery(route.query.plan_q);

const positivePage = value => {
  const page = Number.parseInt(String(value || '1'), 10);
  return Number.isFinite(page) && page > 0 ? page : 1;
};

const accountId = computed(() => route.params.accountId);
const plan = computed(() => summary.value?.plan || {});
const subscription = computed(() => summary.value?.subscription || null);
const action = computed(() => summary.value?.action || {});
const controls = computed(() => summary.value?.controls || {});
const invoices = computed(
  () =>
    summary.value?.invoices || {
      records: [],
      page: 1,
      page_size: INVOICE_PAGE_SIZE,
      total: 0,
    }
);
const eligiblePlans = computed(
  () => summary.value?.eligible_plans || EMPTY_PLAN_PAGE
);
const pendingPlanChange = computed(
  () => summary.value?.pending_plan_change || null
);
const intlLocale = computed(() => platformIntlLocale(locale.value));
const textDirection = computed(() => platformTextDirection(locale.value));
const invoicePage = computed(() => {
  return positivePage(route.query.invoice_page);
});
const planPage = computed(() => positivePage(route.query.plan_page));
const planQuery = computed(() => normalizedPlanQuery(route.query.plan_q));
const summaryContext = computed(() =>
  JSON.stringify([
    String(accountId.value),
    invoicePage.value,
    planPage.value,
    planQuery.value,
  ])
);
const summaryIsCurrent = computed(
  () =>
    Boolean(summary.value) &&
    loadedSummaryContext.value === summaryContext.value
);
const selectedAction = computed(() => String(route.query.action || ''));
const selectedPlanId = computed(() => String(route.query.plan_id || ''));
const planDisplayName = computed(() => displayPlanName(plan.value));
const providerLabel = computed(() => {
  const provider = String(subscription.value?.provider || '').toLowerCase();
  return t(
    `SUBSCRIPTION.PROVIDERS.${PROVIDER_KEYS[provider] || 'MARKETPLACE'}`
  );
});
const paymentState = computed(() => String(route.query.payment || ''));
const callbackPaymentId = computed(() => {
  const primaryId = normalizedPaymentId(route.query.paymentId);
  const fallbackId = normalizedPaymentId(route.query.Id);
  if (primaryId && fallbackId && primaryId !== fallbackId) return '';
  return primaryId || fallbackId;
});
const routeInvoiceId = computed(() =>
  normalizedInvoiceId(route.query.invoice_id)
);
const totalInvoicePages = computed(() =>
  Math.max(1, Math.ceil(invoices.value.total / invoices.value.page_size))
);
const totalPlanPages = computed(() =>
  Math.max(
    1,
    Math.ceil(eligiblePlans.value.total / eligiblePlans.value.page_size)
  )
);
const invoiceRange = computed(() => {
  if (!invoices.value.total) return { start: 0, end: 0 };
  const start = (invoices.value.page - 1) * invoices.value.page_size + 1;
  return {
    start,
    end: Math.min(
      start + invoices.value.records.length - 1,
      invoices.value.total
    ),
  };
});
const selectedPlan = computed(() =>
  eligiblePlans.value.records.find(
    candidate => candidate.id === selectedPlanId.value
  )
);
const planRange = computed(() => {
  if (!eligiblePlans.value.total) return { start: 0, end: 0 };
  const start =
    (eligiblePlans.value.page - 1) * eligiblePlans.value.page_size + 1;
  return {
    start,
    end: Math.min(
      start + eligiblePlans.value.records.length - 1,
      eligiblePlans.value.total
    ),
  };
});
const planSearchPending = computed(
  () => normalizedPlanQuery(planSearchInput.value) !== planQuery.value
);
const paidPlanUnavailable = computed(() =>
  eligiblePlans.value.records.some(planOption =>
    planNeedsUnavailablePayment(planOption)
  )
);
const catalogBusy = computed(() => refreshing.value || planSearchPending.value);
const statusLabel = computed(() =>
  translatedStatus(subscription.value?.status || summary.value?.tenant_status)
);
const sourceLabel = computed(() => {
  if (subscription.value?.billing_source === 'marketplace') {
    return t('SUBSCRIPTION.PLAN.MARKETPLACE', {
      provider: providerLabel.value,
    });
  }
  if (plan.value.free_tier) return t('SUBSCRIPTION.PLAN.FREE');
  if (!subscription.value) return t('SUBSCRIPTION.PLAN.INTERNAL');
  return t('SUBSCRIPTION.PLAN.PORTAL');
});
const renewalTypeLabel = computed(() => {
  if (subscription.value?.cancel_at_period_end) {
    return t('SUBSCRIPTION.DETAILS.CANCELS_AT_PERIOD_END');
  }
  if (subscription.value?.has_recurring_agreement) {
    return t('SUBSCRIPTION.DETAILS.AUTOMATIC');
  }
  if (action.value.type === 'manage_external') {
    return t('SUBSCRIPTION.DETAILS.EXTERNAL', {
      provider: providerLabel.value,
    });
  }
  return t('SUBSCRIPTION.DETAILS.MANUAL');
});
const actionMessage = computed(() => {
  const key = ACTION_REASON_KEYS[action.value.reason];
  if (!key) return t('SUBSCRIPTION.ACTION.PAYMENT_REQUIRED');
  return t(`SUBSCRIPTION.ACTION.${key}`, { provider: providerLabel.value });
});
const renewalActionLabel = computed(() => {
  if (action.value.type === 'activate')
    return t('SUBSCRIPTION.ACTION.ACTIVATE');
  if (
    ['past_due', 'expired', 'canceled'].includes(subscription.value?.status)
  ) {
    return t('SUBSCRIPTION.ACTION.RENEW');
  }
  return t('SUBSCRIPTION.ACTION.EXTEND');
});
const paymentNoticeState = computed(
  () => paymentVerificationState.value || paymentState.value
);
const paymentNotice = computed(() => {
  const key = {
    verifying: 'VERIFYING',
    confirmed: 'CONFIRMED',
    pending: 'PENDING',
    failed: 'FAILED',
    verification_error: 'VERIFICATION_ERROR',
    waiting: 'WAITING',
    error: 'ERROR',
    return: 'VERIFYING',
  }[paymentNoticeState.value];
  return key ? t(`SUBSCRIPTION.PAYMENT.${key}`) : '';
});
const paymentNoticeIcon = computed(() => {
  if (paymentNoticeState.value === 'verifying') {
    return 'i-lucide-loader-circle animate-spin';
  }
  if (paymentNoticeState.value === 'confirmed') return 'i-lucide-circle-check';
  if (
    ['failed', 'verification_error', 'error'].includes(paymentNoticeState.value)
  ) {
    return 'i-lucide-circle-alert';
  }
  return 'i-lucide-clock-3';
});
const paymentNoticeClass = computed(() => {
  if (paymentNoticeState.value === 'confirmed') return 'text-n-teal-11';
  if (
    ['failed', 'verification_error', 'error'].includes(paymentNoticeState.value)
  ) {
    return 'text-n-ruby-11';
  }
  return 'text-n-slate-11';
});
const pendingPaymentInvoiceId = computed(
  () =>
    paymentVerificationInvoiceId.value ||
    routeInvoiceId.value ||
    normalizedInvoiceId(pendingPlanChange.value?.invoice_id)
);
const canCheckPaymentStatus = computed(
  () =>
    Boolean(pendingPaymentInvoiceId.value) &&
    ['pending', 'waiting', 'verification_error'].includes(
      paymentNoticeState.value
    )
);
const actionNotice = computed(() => {
  if (selectedAction.value === 'manual_continuation') {
    return t('SUBSCRIPTION.CANCELLATION.MANUAL_CONTINUATION');
  }
  if (selectedAction.value === 'canceled') {
    return t('SUBSCRIPTION.PLAN_CHANGE.CANCELED');
  }
  return '';
});
const limitRows = computed(() => {
  const limits = plan.value.limits || {};
  const usage = summary.value?.usage || {};
  return [
    {
      key: 'staff',
      label: t('SUBSCRIPTION.LIMITS.STAFF'),
      used: usage.staff,
      limit: limits.staff,
    },
    {
      key: 'inboxes',
      label: t('SUBSCRIPTION.LIMITS.INBOXES'),
      used: usage.inboxes,
      limit: limits.inboxes,
    },
    {
      key: 'whatsapp',
      label: t('SUBSCRIPTION.LIMITS.WHATSAPP'),
      limit: limits.whatsapp_sessions,
    },
    {
      key: 'documents',
      label: t('SUBSCRIPTION.LIMITS.DOCUMENTS'),
      limit: limits.documents,
    },
  ];
});

const displayPlanName = candidate =>
  localizedPlanName({
    locale: locale.value,
    name: candidate?.name,
    nameAr: candidate?.name_ar,
    nameEn: candidate?.name_en,
    code: candidate?.code,
    fallback: t('SUBSCRIPTION.PLAN.DEFAULT_NAME'),
  });

const translatedStatus = status => {
  const key = STATUS_KEYS[String(status || '').toLowerCase()] || 'MANAGED';
  return t(`SUBSCRIPTION.STATUS.${key}`);
};

const invoicePlanChangeLabel = invoice => {
  const status = String(invoice?.plan_change_status || '').toUpperCase();
  if (!status) return '';
  return t(`SUBSCRIPTION.INVOICES.PLAN_CHANGE_${status}`, {
    plan: displayPlanName(invoice.target_plan),
  });
};

const invoicePlanChangeClass = invoice =>
  invoice?.plan_change_status === 'not_applied'
    ? 'text-n-ruby-11'
    : 'text-n-slate-10';

const invoicePaymentActionAvailable = invoice =>
  controls.value.payment_available &&
  !invoice?.target_plan &&
  ['checkout_created', 'pending', 'indeterminate', 'manual_reconcile'].includes(
    String(invoice?.status || '').toLowerCase()
  );

const dateFromTimestamp = timestamp => {
  if (!timestamp) return null;
  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime()) || date.getUTCFullYear() <= 1) return null;
  return date;
};

const localizedDate = date =>
  new Intl.DateTimeFormat(intlLocale.value, {
    dateStyle: 'long',
    timeZone: 'Africa/Cairo',
  }).format(date);

const formatDate = timestamp => {
  const date = dateFromTimestamp(timestamp);
  if (!date) return t('SUBSCRIPTION.COMMON.NOT_SET');
  return localizedDate(date);
};

const scheduledPlanChangeDate = computed(() => {
  const date = dateFromTimestamp(pendingPlanChange.value?.scheduled_for);
  return date ? localizedDate(date) : '';
});

const formatMoney = (amountCents, currency, freeTier = false) => {
  if (Number(amountCents || 0) === 0 && freeTier) {
    return t('SUBSCRIPTION.PLAN.FREE');
  }
  return new Intl.NumberFormat(intlLocale.value, {
    style: 'currency',
    currency: currency || 'SAR',
    maximumFractionDigits: 2,
  }).format(Number(amountCents || 0) / 100);
};

const formatLimit = row => {
  const limit = Number(row.limit || 0);
  if (limit === 0) return t('SUBSCRIPTION.LIMITS.UNLIMITED');
  if (row.used === undefined) {
    return new Intl.NumberFormat(intlLocale.value).format(limit);
  }
  return t('SUBSCRIPTION.LIMITS.USED_OF', {
    used: new Intl.NumberFormat(intlLocale.value).format(row.used),
    limit: new Intl.NumberFormat(intlLocale.value).format(limit),
  });
};

const candidateLimit = (candidate, key) => {
  const limit = Number(candidate.limits?.[key] || 0);
  return limit === 0
    ? t('SUBSCRIPTION.LIMITS.UNLIMITED')
    : new Intl.NumberFormat(intlLocale.value).format(limit);
};

const planNeedsUnavailablePayment = candidate =>
  !candidate.free_tier && !controls.value.payment_available;

const updateURLState = changes => {
  const query = { ...route.query, ...changes };
  Object.keys(query).forEach(key => {
    if (query[key] === '' || query[key] === undefined || query[key] === null) {
      delete query[key];
    }
  });
  return router.replace({ query });
};

const clearPlanSearchTimers = () => {
  window.clearTimeout(planSearchDebounceTimer);
  window.clearTimeout(planSearchMaxTimer);
  planSearchDebounceTimer = undefined;
  planSearchMaxTimer = undefined;
};

const applyPlanSearch = () => {
  clearPlanSearchTimers();
  const query = normalizedPlanQuery(planSearchInput.value);
  if (query === planQuery.value) return;
  updateURLState({ plan_q: query, plan_page: undefined, plan_id: undefined });
};

const schedulePlanSearch = () => {
  window.clearTimeout(planSearchDebounceTimer);
  planSearchDebounceTimer = window.setTimeout(
    applyPlanSearch,
    PLAN_SEARCH_DEBOUNCE_MS
  );
  planSearchMaxTimer ||= window.setTimeout(
    applyPlanSearch,
    PLAN_SEARCH_MAX_WAIT_MS
  );
};

const openAction = actionName => {
  mutationError.value = '';
  if (actionName === 'change_plan')
    return updateURLState({ action: actionName });

  clearPlanSearchTimers();
  planSearchInput.value = '';
  return updateURLState({
    action: actionName,
    plan_page: undefined,
    plan_q: undefined,
    plan_id: undefined,
  });
};

const closeAction = () => openAction('');

const isCurrentSummaryRequest = (generation, context) =>
  generation === summaryRequestGeneration && context === summaryContext.value;

const isCurrentMutationRequest = (generation, context) =>
  generation === mutationRequestGeneration && context === summaryContext.value;

const invalidatePendingRequests = () => {
  summaryRequestGeneration += 1;
  mutationRequestGeneration += 1;
};

const loadSummary = async () => {
  const requestGeneration = ++summaryRequestGeneration;
  const requestContext = summaryContext.value;
  const requestAccountId = accountId.value;
  const requestInvoicePage = invoicePage.value;
  const requestPlanPage = planPage.value;
  const requestPlanQuery = planQuery.value;
  loading.value = !summary.value;
  refreshing.value = Boolean(summary.value);
  loadError.value = false;
  try {
    const response = await loadSubscriptionManagement(requestAccountId, {
      invoicePage: requestInvoicePage,
      invoicePageSize: INVOICE_PAGE_SIZE,
      planPage: requestPlanPage,
      planPageSize: PLAN_PAGE_SIZE,
      planQuery: requestPlanQuery,
    });
    if (!isCurrentSummaryRequest(requestGeneration, requestContext)) return;
    summary.value = response.data;
    loadedSummaryContext.value = requestContext;
    const correctedPages = {};
    if (invoices.value.page > totalInvoicePages.value) {
      correctedPages.invoice_page = totalInvoicePages.value;
    }
    if (eligiblePlans.value.page > totalPlanPages.value) {
      correctedPages.plan_page = totalPlanPages.value;
      correctedPages.plan_id = undefined;
    }
    if (Object.keys(correctedPages).length)
      await updateURLState(correctedPages);
  } catch {
    if (!isCurrentSummaryRequest(requestGeneration, requestContext)) return;
    loadError.value = true;
  } finally {
    if (isCurrentSummaryRequest(requestGeneration, requestContext)) {
      loading.value = false;
      refreshing.value = false;
    }
  }
};

const clearPaymentPoll = () => {
  window.clearTimeout(paymentPollTimer);
  paymentPollTimer = undefined;
  paymentPollAttempts = 0;
};

const paymentInvoiceStatus = invoiceId =>
  invoices.value.records.find(invoice => invoice.id === invoiceId)?.status ||
  '';

const schedulePaymentPoll = invoiceId => {
  clearPaymentPoll();
  const poll = async () => {
    paymentPollAttempts += 1;
    await loadSummary();
    if (paymentInvoiceStatus(invoiceId) === 'paid') {
      paymentVerificationState.value = 'confirmed';
      await updateURLState({ payment: 'confirmed', invoice_id: invoiceId });
      clearPaymentPoll();
      return;
    }
    if (paymentPollAttempts < PAYMENT_POLL_MAX_ATTEMPTS) {
      paymentPollTimer = window.setTimeout(poll, PAYMENT_POLL_INTERVAL_MS);
    }
  };
  paymentPollTimer = window.setTimeout(poll, PAYMENT_POLL_INTERVAL_MS);
};

const scrubProviderPaymentQuery = (status, invoiceId) =>
  updateURLState({
    action: 'payment_status',
    payment: status,
    invoice_id: invoiceId || undefined,
    invoice_page: 1,
    paymentId: undefined,
    Id: undefined,
  });

const applyVerifiedPayment = async response => {
  const invoiceId = normalizedInvoiceId(response.data?.invoice_id);
  const state = String(response.data?.status || 'pending');
  paymentVerificationInvoiceId.value = invoiceId;
  paymentVerificationState.value = state;
  await scrubProviderPaymentQuery(state, invoiceId);
  await loadSummary();
  if (invoiceId && ['confirmed', 'pending'].includes(state)) {
    schedulePaymentPoll(invoiceId);
  }
};

const applyPaymentVerificationFailure = async (error, invoiceId) => {
  paymentVerificationState.value = 'verification_error';
  mutationError.value =
    error?.response?.data?.error || 'payment_status_unavailable';
  await scrubProviderPaymentQuery('verification_error', invoiceId);
};

const verifyPaymentStatus = async ({ paymentId = '', invoiceId = '' } = {}) => {
  if (mutationLoading.value || (!paymentId && !invoiceId)) return;
  mutationLoading.value = 'sync_payment';
  paymentVerificationState.value = 'verifying';
  mutationError.value = '';
  try {
    const response = await syncSubscriptionPayment(
      accountId.value,
      paymentId,
      invoiceId
    );
    await applyVerifiedPayment(response);
  } catch (error) {
    await applyPaymentVerificationFailure(error, invoiceId);
  } finally {
    mutationLoading.value = '';
  }
};

const verifyReturnedPayment = () => {
  if (paymentState.value !== 'return') return;
  const key = `${accountId.value}:${callbackPaymentId.value}:${routeInvoiceId.value}`;
  if (automaticPaymentSyncKey === key) return;
  automaticPaymentSyncKey = key;
  if (!callbackPaymentId.value && !routeInvoiceId.value) {
    paymentVerificationState.value = 'verification_error';
    scrubProviderPaymentQuery('verification_error', '');
    return;
  }
  verifyPaymentStatus({
    paymentId: callbackPaymentId.value,
    invoiceId: routeInvoiceId.value,
  });
};

const mutationErrorMessage = code => {
  const key = String(code || 'subscription_action_failed').toUpperCase();
  const supported = [
    'PAYMENT_GATEWAY_UNAVAILABLE',
    'RENEWAL_UNAVAILABLE',
    'SUBSCRIPTION_PLAN_INELIGIBLE',
    'SUBSCRIPTION_PLAN_CHANGE_PENDING',
    'SUBSCRIPTION_CANCELLATION_NOT_ALLOWED',
    'SUBSCRIPTION_PROVIDER_CANCEL_ERROR',
    'SUBSCRIPTION_ACCOUNT_RESTRICTED',
    'PAYMENT_STATUS_MISMATCH',
    'PAYMENT_STATUS_UNAVAILABLE',
  ];
  return t(
    `SUBSCRIPTION.ERRORS.${supported.includes(key) ? key : 'ACTION_FAILED'}`
  );
};

const checkoutURLFromMutation = response => {
  const checkoutURL = safeRenewalCheckoutURL(response.data?.url);
  if (response.data?.url && !checkoutURL) {
    throw new Error('checkout_url_invalid');
  }
  return checkoutURL;
};

const startMutationCheckout = async response => {
  const checkoutURL = checkoutURLFromMutation(response);
  if (!checkoutURL) return false;
  const invoiceId = normalizedInvoiceId(response.data?.invoice_id);
  paymentVerificationInvoiceId.value = invoiceId;
  paymentVerificationState.value = 'waiting';
  await updateURLState({
    action: 'payment_status',
    payment: 'waiting',
    invoice_id: invoiceId || undefined,
    invoice_page: 1,
    plan_id: undefined,
  });
  window.location.assign(checkoutURL);
  return true;
};

const runMutation = async (actionName, planId = '') => {
  if (loading.value || mutationLoading.value || !summaryIsCurrent.value) return;
  const requestGeneration = ++mutationRequestGeneration;
  const requestContext = summaryContext.value;
  const requestAccountId = accountId.value;
  mutationLoading.value = actionName;
  mutationError.value = '';
  try {
    const response = await mutateSubscription(
      requestAccountId,
      actionName,
      planId
    );
    if (!isCurrentMutationRequest(requestGeneration, requestContext)) {
      return;
    }
    if (await startMutationCheckout(response)) return;
    await updateURLState({ action: response.data?.status || '' });
    await loadSummary();
  } catch (error) {
    if (isCurrentMutationRequest(requestGeneration, requestContext)) {
      mutationError.value =
        error?.response?.data?.error || 'subscription_action_failed';
    }
  } finally {
    if (isCurrentMutationRequest(requestGeneration, requestContext)) {
      mutationLoading.value = '';
    }
  }
};

const openMarketplaceOwner = async () => {
  if (loading.value || mutationLoading.value || !summaryIsCurrent.value) return;
  const requestGeneration = ++mutationRequestGeneration;
  const requestContext = summaryContext.value;
  const requestAccountId = accountId.value;
  mutationLoading.value = 'marketplace';
  mutationError.value = '';
  try {
    const response = await requestSubscriptionRenewal(requestAccountId);
    if (!isCurrentMutationRequest(requestGeneration, requestContext)) return;
    const ownerURL = safeRenewalCheckoutURL(response.data?.url);
    if (!ownerURL) throw new Error('owner_url_invalid');
    window.location.assign(ownerURL);
  } catch (error) {
    if (isCurrentMutationRequest(requestGeneration, requestContext)) {
      mutationError.value =
        error?.response?.data?.error || 'subscription_action_failed';
    }
  } finally {
    if (isCurrentMutationRequest(requestGeneration, requestContext)) {
      mutationLoading.value = '';
    }
  }
};

const selectInvoicePage = pageNumber => {
  if (
    !summaryIsCurrent.value ||
    pageNumber < 1 ||
    pageNumber > totalInvoicePages.value
  ) {
    return;
  }
  updateURLState({ invoice_page: pageNumber });
};

const selectPlanPage = pageNumber => {
  if (
    catalogBusy.value ||
    !summaryIsCurrent.value ||
    pageNumber < 1 ||
    pageNumber > totalPlanPages.value
  ) {
    return;
  }
  updateURLState({ plan_page: pageNumber, plan_id: undefined });
};

const selectPlan = candidate => {
  if (
    catalogBusy.value ||
    !summaryIsCurrent.value ||
    planNeedsUnavailablePayment(candidate)
  ) {
    return;
  }
  updateURLState({ plan_id: candidate.id });
};

const reloadForRouteContext = (currentContext, previousContext = []) => {
  invalidatePendingRequests();
  if (previousContext.length && currentContext[0] !== previousContext[0]) {
    summary.value = null;
    loadedSummaryContext.value = '';
  }
  mutationLoading.value = '';
  mutationError.value = '';
  loadSummary();
};

watch(planSearchInput, () => {
  if (normalizedPlanQuery(planSearchInput.value) === planQuery.value) {
    clearPlanSearchTimers();
    return;
  }
  schedulePlanSearch();
});
watch(planQuery, query => {
  if (normalizedPlanQuery(planSearchInput.value) === query) return;
  clearPlanSearchTimers();
  planSearchInput.value = query;
});
watch([accountId, invoicePage, planPage, planQuery], reloadForRouteContext, {
  immediate: true,
});
watch(
  [accountId, paymentState, callbackPaymentId, routeInvoiceId],
  verifyReturnedPayment,
  { immediate: true }
);
onBeforeUnmount(() => {
  clearPlanSearchTimers();
  clearPaymentPoll();
  invalidatePendingRequests();
});
</script>

<template>
  <SettingsLayout
    :is-loading="loading"
    :loading-message="t('SUBSCRIPTION.LOADING')"
  >
    <template #header>
      <header
        class="flex flex-wrap items-start justify-between gap-3 py-2"
        :dir="textDirection"
      >
        <div>
          <p class="text-xs font-medium text-n-slate-10">
            {{ t('SUBSCRIPTION.ACCOUNT_SETTINGS') }}
          </p>
          <h1 class="mt-1 text-xl font-semibold text-n-slate-12">
            {{ t('SUBSCRIPTION.TITLE') }}
          </h1>
          <p class="mt-1 text-sm text-n-slate-10">
            {{ t('SUBSCRIPTION.DESCRIPTION') }}
          </p>
        </div>
        <span
          v-if="summaryIsCurrent"
          class="inline-flex min-h-7 items-center rounded-md border border-n-weak bg-n-alpha-2 px-2.5 text-xs font-medium text-n-slate-11"
        >
          {{ statusLabel }}
        </span>
      </header>
    </template>

    <template #body>
      <div :dir="textDirection">
        <p
          v-if="loadError"
          class="border-y border-n-weak py-5 text-sm text-n-ruby-11"
          role="alert"
        >
          {{ t('SUBSCRIPTION.ERRORS.LOAD') }}
        </p>

        <template v-if="summary">
          <p
            v-if="summary.billing_available === false"
            class="mb-5 border-y border-n-weak bg-n-alpha-2 px-4 py-3 text-sm text-n-slate-11"
            role="status"
          >
            {{ t('SUBSCRIPTION.STANDALONE_UNAVAILABLE') }}
          </p>
          <div
            v-if="paymentNotice"
            class="mb-5 flex flex-wrap items-center justify-between gap-3 border-y border-n-weak bg-n-alpha-2 px-4 py-3"
            role="status"
          >
            <span
              class="flex items-start gap-2 text-sm"
              :class="paymentNoticeClass"
            >
              <span class="mt-0.5 size-4 shrink-0" :class="paymentNoticeIcon" />
              <span>{{ paymentNotice }}</span>
            </span>
            <button
              class="inline-flex min-h-10 items-center gap-2 text-sm font-medium text-n-blue-11 disabled:opacity-50"
              type="button"
              :disabled="
                paymentNoticeState === 'verifying' ||
                mutationLoading === 'sync_payment'
              "
              @click="
                canCheckPaymentStatus
                  ? verifyPaymentStatus({ invoiceId: pendingPaymentInvoiceId })
                  : loadSummary()
              "
            >
              <span class="i-lucide-refresh-cw size-4" />
              {{
                canCheckPaymentStatus
                  ? t('SUBSCRIPTION.PAYMENT.CHECK_STATUS')
                  : t('SUBSCRIPTION.PAYMENT.REFRESH')
              }}
            </button>
          </div>
          <p
            v-if="actionNotice"
            class="mb-5 border-y border-n-weak bg-n-alpha-2 px-4 py-3 text-sm text-n-slate-11"
            role="status"
          >
            {{ actionNotice }}
          </p>

          <section class="border-y border-n-weak py-6">
            <div class="flex flex-wrap items-start justify-between gap-5">
              <div>
                <p class="text-xs text-n-slate-10">
                  {{ t('SUBSCRIPTION.PLAN.CURRENT') }}
                </p>
                <h2 class="mt-1 text-lg font-semibold text-n-slate-12">
                  {{ planDisplayName }}
                </h2>
                <p class="mt-1 text-sm text-n-slate-10">{{ sourceLabel }}</p>
              </div>
              <div class="text-end">
                <p class="text-xs text-n-slate-10">
                  {{ t('SUBSCRIPTION.PLAN.PERIOD_VALUE') }}
                </p>
                <p class="mt-1 text-lg font-semibold text-n-slate-12">
                  {{
                    formatMoney(
                      subscription?.amount_cents,
                      subscription?.currency,
                      plan.free_tier
                    )
                  }}
                </p>
              </div>
            </div>

            <dl class="mt-6 grid gap-x-8 gap-y-5 sm:grid-cols-2 lg:grid-cols-4">
              <div>
                <dt class="text-xs text-n-slate-10">
                  {{ t('SUBSCRIPTION.DETAILS.STATUS') }}
                </dt>
                <dd class="mt-1 text-sm font-medium text-n-slate-12">
                  {{ statusLabel }}
                </dd>
              </div>
              <div>
                <dt class="text-xs text-n-slate-10">
                  {{ t('SUBSCRIPTION.DETAILS.PERIOD_START') }}
                </dt>
                <dd class="mt-1 text-sm font-medium text-n-slate-12">
                  {{ formatDate(subscription?.period_start_at) }}
                </dd>
              </div>
              <div>
                <dt class="text-xs text-n-slate-10">
                  {{ t('SUBSCRIPTION.DETAILS.PERIOD_END') }}
                </dt>
                <dd class="mt-1 text-sm font-medium text-n-slate-12">
                  {{ formatDate(subscription?.period_end_at) }}
                </dd>
              </div>
              <div>
                <dt class="text-xs text-n-slate-10">
                  {{ t('SUBSCRIPTION.DETAILS.RENEWAL') }}
                </dt>
                <dd class="mt-1 text-sm font-medium text-n-slate-12">
                  {{ renewalTypeLabel }}
                </dd>
              </div>
            </dl>

            <div
              v-if="pendingPlanChange"
              class="mt-5 flex flex-wrap items-center justify-between gap-3 border-y border-n-weak bg-n-alpha-2 px-4 py-3"
            >
              <div>
                <p class="text-sm font-medium text-n-slate-12">
                  {{
                    t('SUBSCRIPTION.PLAN_CHANGE.PENDING_TITLE', {
                      plan: displayPlanName(pendingPlanChange.plan),
                    })
                  }}
                </p>
                <p class="mt-1 text-xs text-n-slate-10">
                  {{
                    pendingPlanChange.status === 'paid'
                      ? t('SUBSCRIPTION.PLAN_CHANGE.PROCESSING')
                      : t('SUBSCRIPTION.PLAN_CHANGE.AWAITING_PAYMENT')
                  }}
                  <span v-if="scheduledPlanChangeDate">
                    {{
                      t('SUBSCRIPTION.PLAN_CHANGE.EFFECTIVE_ON', {
                        date: scheduledPlanChangeDate,
                      })
                    }}
                  </span>
                </p>
              </div>
              <div class="flex flex-wrap items-center gap-2">
                <button
                  v-if="
                    pendingPlanChange.invoice_id &&
                    pendingPlanChange.status !== 'paid'
                  "
                  type="button"
                  class="inline-flex min-h-10 items-center gap-2 rounded-md bg-n-blue-9 px-4 text-sm font-medium text-white disabled:opacity-60"
                  :disabled="Boolean(mutationLoading)"
                  @click="
                    verifyPaymentStatus({
                      invoiceId: pendingPlanChange.invoice_id,
                    })
                  "
                >
                  <span
                    class="size-4"
                    :class="
                      mutationLoading === 'sync_payment'
                        ? 'i-lucide-loader-circle animate-spin'
                        : 'i-lucide-refresh-cw'
                    "
                  />
                  {{ t('SUBSCRIPTION.PAYMENT.CHECK_STATUS') }}
                </button>
                <button
                  v-if="
                    pendingPlanChange.invoice_id &&
                    pendingPlanChange.status !== 'paid'
                  "
                  type="button"
                  class="inline-flex min-h-10 items-center gap-2 rounded-md border border-n-weak px-4 text-sm font-medium text-n-slate-12 disabled:opacity-60"
                  :disabled="
                    Boolean(mutationLoading) || !controls.payment_available
                  "
                  @click="runMutation('change_plan', pendingPlanChange.plan.id)"
                >
                  <span class="i-lucide-external-link size-4" />
                  {{ t('SUBSCRIPTION.PLAN_CHANGE.CONTINUE_PAYMENT') }}
                </button>
                <button
                  v-if="controls.can_cancel_plan_change"
                  type="button"
                  class="inline-flex min-h-10 items-center gap-2 rounded-md border border-n-weak px-4 text-sm font-medium text-n-ruby-11 disabled:opacity-60"
                  :disabled="Boolean(mutationLoading)"
                  @click="runMutation('cancel_plan_change')"
                >
                  <span class="i-lucide-x size-4" />
                  {{ t('SUBSCRIPTION.PLAN_CHANGE.CANCEL') }}
                </button>
              </div>
            </div>

            <div
              class="mt-6 flex flex-wrap items-center justify-between gap-4 border-t border-n-weak pt-5"
            >
              <p class="max-w-2xl text-sm text-n-slate-11">
                {{ actionMessage }}
              </p>
              <div class="flex flex-wrap items-center gap-2">
                <button
                  v-if="controls.can_change_plan"
                  type="button"
                  class="inline-flex min-h-10 items-center gap-2 rounded-md bg-n-blue-9 px-4 text-sm font-medium text-white disabled:opacity-60"
                  :disabled="!summaryIsCurrent"
                  @click="openAction('change_plan')"
                >
                  <span class="i-lucide-repeat-2 size-4" />
                  {{ t('SUBSCRIPTION.ACTION.CHANGE_PLAN') }}
                </button>
                <button
                  v-if="controls.can_renew"
                  type="button"
                  class="inline-flex min-h-10 items-center gap-2 rounded-md border border-n-weak px-4 text-sm font-medium text-n-slate-12 disabled:opacity-60"
                  :disabled="Boolean(mutationLoading) || !summaryIsCurrent"
                  @click="runMutation('renew')"
                >
                  <span class="i-lucide-calendar-plus size-4" />
                  {{ renewalActionLabel }}
                </button>
                <button
                  v-if="controls.can_cancel"
                  type="button"
                  class="inline-flex min-h-10 items-center gap-2 rounded-md border border-n-weak px-4 text-sm font-medium text-n-ruby-11 disabled:opacity-60"
                  :disabled="!summaryIsCurrent"
                  @click="openAction('cancel_at_period_end')"
                >
                  <span class="i-lucide-calendar-x size-4" />
                  {{ t('SUBSCRIPTION.ACTION.CANCEL') }}
                </button>
                <button
                  v-if="controls.can_undo_cancellation"
                  type="button"
                  class="inline-flex min-h-10 items-center gap-2 rounded-md border border-n-weak px-4 text-sm font-medium text-n-slate-12 disabled:opacity-60"
                  :disabled="Boolean(mutationLoading) || !summaryIsCurrent"
                  @click="runMutation('undo_cancellation')"
                >
                  <span class="i-lucide-rotate-ccw size-4" />
                  {{ t('SUBSCRIPTION.ACTION.UNDO_CANCELLATION') }}
                </button>
                <button
                  v-if="action.type === 'manage_external'"
                  type="button"
                  class="inline-flex min-h-10 items-center gap-2 rounded-md bg-n-blue-9 px-4 text-sm font-medium text-white disabled:opacity-60"
                  :disabled="Boolean(mutationLoading) || !summaryIsCurrent"
                  @click="openMarketplaceOwner"
                >
                  <span class="i-lucide-external-link size-4" />
                  {{
                    t('SUBSCRIPTION.ACTION.MANAGE_EXTERNAL', {
                      provider: providerLabel,
                    })
                  }}
                </button>
              </div>
            </div>
            <p
              v-if="mutationError"
              class="mt-3 text-sm text-n-ruby-11"
              role="alert"
            >
              {{ mutationErrorMessage(mutationError) }}
            </p>
          </section>

          <section
            v-if="selectedAction === 'change_plan'"
            class="border-b border-n-weak py-6"
            aria-labelledby="change-plan-title"
          >
            <div class="flex items-start justify-between gap-3">
              <div>
                <h2
                  id="change-plan-title"
                  class="text-sm font-semibold text-n-slate-12"
                >
                  {{ t('SUBSCRIPTION.PLAN_CHANGE.TITLE') }}
                </h2>
                <p class="mt-1 text-xs text-n-slate-10">
                  {{ t('SUBSCRIPTION.PLAN_CHANGE.DESCRIPTION') }}
                </p>
              </div>
              <button
                type="button"
                class="inline-flex size-10 items-center justify-center text-n-slate-10"
                :aria-label="t('SUBSCRIPTION.ACTION.CLOSE')"
                @click="closeAction"
              >
                <span class="i-lucide-x size-4" />
              </button>
            </div>
            <div class="mt-5 flex flex-wrap items-end justify-between gap-3">
              <label class="relative block w-full max-w-md">
                <span class="sr-only">{{
                  t('SUBSCRIPTION.PLAN_CHANGE.SEARCH_LABEL')
                }}</span>
                <span
                  class="i-lucide-search pointer-events-none absolute start-3 top-1/2 size-4 -translate-y-1/2 text-n-slate-9"
                />
                <input
                  v-model="planSearchInput"
                  type="search"
                  class="h-10 w-full rounded-md border border-n-weak bg-n-background pe-10 ps-9 text-sm text-n-slate-12 outline-none focus:border-n-blue-9"
                  :placeholder="
                    t('SUBSCRIPTION.PLAN_CHANGE.SEARCH_PLACEHOLDER')
                  "
                  :aria-label="t('SUBSCRIPTION.PLAN_CHANGE.SEARCH_LABEL')"
                  @keydown.enter.prevent="applyPlanSearch"
                />
                <button
                  v-if="planSearchInput"
                  type="button"
                  class="absolute end-1 top-1 inline-flex size-8 items-center justify-center text-n-slate-9 hover:text-n-slate-12"
                  :aria-label="t('SUBSCRIPTION.PLAN_CHANGE.CLEAR_SEARCH')"
                  @click="planSearchInput = ''"
                >
                  <span class="i-lucide-x size-4" />
                </button>
              </label>
              <p class="text-xs tabular-nums text-n-slate-10">
                {{
                  t('SUBSCRIPTION.PLAN_CHANGE.RESULTS', {
                    start: planRange.start,
                    end: planRange.end,
                    total: eligiblePlans.total,
                  })
                }}
              </p>
            </div>

            <div
              v-if="eligiblePlans.records.length"
              class="mt-4 grid gap-3 md:grid-cols-2"
              :class="catalogBusy ? 'opacity-60' : ''"
            >
              <button
                v-for="candidate in eligiblePlans.records"
                :key="candidate.id"
                type="button"
                class="min-h-52 rounded-md border p-4 text-start transition-colors disabled:cursor-not-allowed disabled:opacity-50"
                :class="
                  selectedPlanId === candidate.id
                    ? 'border-n-blue-9 bg-n-alpha-2'
                    : 'border-n-weak hover:border-n-slate-7 hover:bg-n-alpha-1'
                "
                :disabled="
                  catalogBusy || planNeedsUnavailablePayment(candidate)
                "
                @click="selectPlan(candidate)"
              >
                <span class="flex items-start justify-between gap-4">
                  <span class="flex min-w-0 items-start gap-3">
                    <span
                      :class="
                        selectedPlanId === candidate.id
                          ? 'i-lucide-circle-check text-n-blue-10'
                          : 'i-lucide-circle text-n-slate-8'
                      "
                      class="mt-0.5 size-5 shrink-0"
                    />
                    <span class="min-w-0">
                      <span
                        class="block break-words text-base font-semibold text-n-slate-12"
                      >
                        {{ displayPlanName(candidate) }}
                      </span>
                      <span class="mt-1 block text-xs text-n-slate-10">
                        {{
                          candidate.free_tier
                            ? t('SUBSCRIPTION.PLAN_CHANGE.NO_PAYMENT')
                            : t('SUBSCRIPTION.PLAN_CHANGE.PAYMENT_REQUIRED')
                        }}
                      </span>
                    </span>
                  </span>
                  <span
                    class="shrink-0 text-base font-semibold tabular-nums text-n-slate-12"
                  >
                    {{
                      formatMoney(
                        candidate.amount_cents,
                        candidate.currency,
                        candidate.free_tier
                      )
                    }}
                  </span>
                </span>
                <dl
                  class="mt-5 grid grid-cols-2 gap-x-5 gap-y-3 border-t border-n-weak pt-4 text-xs"
                >
                  <div>
                    <dt class="text-n-slate-10">
                      {{ t('SUBSCRIPTION.LIMITS.STAFF') }}
                    </dt>
                    <dd class="mt-1 font-medium text-n-slate-12">
                      {{ candidateLimit(candidate, 'staff') }}
                    </dd>
                  </div>
                  <div>
                    <dt class="text-n-slate-10">
                      {{ t('SUBSCRIPTION.LIMITS.INBOXES') }}
                    </dt>
                    <dd class="mt-1 font-medium text-n-slate-12">
                      {{ candidateLimit(candidate, 'inboxes') }}
                    </dd>
                  </div>
                  <div>
                    <dt class="text-n-slate-10">
                      {{ t('SUBSCRIPTION.LIMITS.WHATSAPP') }}
                    </dt>
                    <dd class="mt-1 font-medium text-n-slate-12">
                      {{ candidateLimit(candidate, 'whatsapp_sessions') }}
                    </dd>
                  </div>
                  <div>
                    <dt class="text-n-slate-10">
                      {{ t('SUBSCRIPTION.LIMITS.DOCUMENTS') }}
                    </dt>
                    <dd class="mt-1 font-medium text-n-slate-12">
                      {{ candidateLimit(candidate, 'documents') }}
                    </dd>
                  </div>
                </dl>
              </button>
            </div>

            <p
              v-else
              class="mt-4 border-y border-n-weak py-6 text-sm text-n-slate-10"
            >
              {{
                planQuery
                  ? t('SUBSCRIPTION.PLAN_CHANGE.EMPTY_SEARCH')
                  : t('SUBSCRIPTION.PLAN_CHANGE.EMPTY')
              }}
            </p>
            <p v-if="paidPlanUnavailable" class="mt-3 text-sm text-n-ruby-11">
              {{ t('SUBSCRIPTION.ERRORS.PAYMENT_GATEWAY_UNAVAILABLE') }}
            </p>

            <nav
              v-if="eligiblePlans.total > eligiblePlans.page_size"
              class="mt-4 flex items-center justify-between gap-3"
              :aria-label="t('SUBSCRIPTION.PLAN_CHANGE.PAGINATION_LABEL')"
            >
              <button
                type="button"
                class="inline-flex min-h-10 items-center gap-2 rounded-md border border-n-weak px-3 text-sm font-medium text-n-slate-12 disabled:opacity-40"
                :disabled="catalogBusy || eligiblePlans.page <= 1"
                @click="selectPlanPage(eligiblePlans.page - 1)"
              >
                <span class="i-lucide-chevron-left size-4 rtl:rotate-180" />
                {{ t('SUBSCRIPTION.PLAN_CHANGE.PREVIOUS') }}
              </button>
              <span class="text-xs tabular-nums text-n-slate-10">{{
                t('SUBSCRIPTION.PLAN_CHANGE.PAGE', {
                  page: eligiblePlans.page,
                  pages: totalPlanPages,
                })
              }}</span>
              <button
                type="button"
                class="inline-flex min-h-10 items-center gap-2 rounded-md border border-n-weak px-3 text-sm font-medium text-n-slate-12 disabled:opacity-40"
                :disabled="catalogBusy || eligiblePlans.page >= totalPlanPages"
                @click="selectPlanPage(eligiblePlans.page + 1)"
              >
                {{ t('SUBSCRIPTION.PLAN_CHANGE.NEXT') }}
                <span class="i-lucide-chevron-right size-4 rtl:rotate-180" />
              </button>
            </nav>
            <div class="mt-5 flex flex-wrap justify-end gap-2">
              <button
                type="button"
                class="inline-flex min-h-10 items-center rounded-md border border-n-weak px-4 text-sm font-medium text-n-slate-12"
                @click="closeAction"
              >
                {{ t('SUBSCRIPTION.ACTION.CLOSE') }}
              </button>
              <button
                type="button"
                class="inline-flex min-h-10 items-center gap-2 rounded-md bg-n-blue-9 px-4 text-sm font-medium text-white disabled:opacity-60"
                :disabled="
                  !selectedPlan ||
                  catalogBusy ||
                  Boolean(mutationLoading) ||
                  planNeedsUnavailablePayment(selectedPlan)
                "
                @click="runMutation('change_plan', selectedPlan.id)"
              >
                <span class="i-lucide-check size-4" />
                {{
                  mutationLoading
                    ? t('SUBSCRIPTION.ACTION.PREPARING')
                    : t('SUBSCRIPTION.PLAN_CHANGE.CONFIRM')
                }}
              </button>
            </div>
          </section>

          <section
            v-if="selectedAction === 'cancel_at_period_end'"
            class="border-b border-n-weak py-6"
            aria-labelledby="cancel-title"
          >
            <h2 id="cancel-title" class="text-sm font-semibold text-n-slate-12">
              {{ t('SUBSCRIPTION.CANCELLATION.TITLE') }}
            </h2>
            <p class="mt-2 max-w-2xl text-sm text-n-slate-11">
              {{
                t('SUBSCRIPTION.CANCELLATION.DESCRIPTION', {
                  date: formatDate(subscription?.period_end_at),
                })
              }}
            </p>
            <div class="mt-5 flex flex-wrap gap-2">
              <button
                type="button"
                class="inline-flex min-h-10 items-center gap-2 rounded-md bg-n-ruby-9 px-4 text-sm font-medium text-white disabled:opacity-60"
                :disabled="Boolean(mutationLoading)"
                @click="runMutation('cancel_at_period_end')"
              >
                <span class="i-lucide-calendar-x size-4" />
                {{
                  mutationLoading
                    ? t('SUBSCRIPTION.ACTION.PREPARING')
                    : t('SUBSCRIPTION.CANCELLATION.CONFIRM')
                }}
              </button>
              <button
                type="button"
                class="inline-flex min-h-10 items-center rounded-md border border-n-weak px-4 text-sm font-medium text-n-slate-12"
                @click="closeAction"
              >
                {{ t('SUBSCRIPTION.ACTION.CLOSE') }}
              </button>
            </div>
          </section>

          <section class="border-b border-n-weak py-6">
            <div class="mb-4">
              <h2 class="text-sm font-semibold text-n-slate-12">
                {{ t('SUBSCRIPTION.LIMITS.TITLE') }}
              </h2>
              <p class="mt-1 text-xs text-n-slate-10">
                {{ t('SUBSCRIPTION.LIMITS.DESCRIPTION') }}
              </p>
            </div>
            <dl
              class="grid gap-px overflow-hidden rounded-md border border-n-weak bg-n-weak sm:grid-cols-2 lg:grid-cols-4"
            >
              <div
                v-for="row in limitRows"
                :key="row.key"
                class="bg-n-background px-4 py-3"
              >
                <dt class="text-xs text-n-slate-10">{{ row.label }}</dt>
                <dd class="mt-1 text-sm font-medium text-n-slate-12">
                  {{ formatLimit(row) }}
                </dd>
              </div>
            </dl>
          </section>

          <section class="py-6">
            <div class="mb-4 flex flex-wrap items-end justify-between gap-3">
              <div>
                <h2 class="text-sm font-semibold text-n-slate-12">
                  {{ t('SUBSCRIPTION.INVOICES.TITLE') }}
                </h2>
                <p class="mt-1 text-xs text-n-slate-10">
                  {{
                    t('SUBSCRIPTION.INVOICES.SUMMARY', {
                      start: invoiceRange.start,
                      end: invoiceRange.end,
                      total: invoices.total,
                    })
                  }}
                </p>
              </div>
              <span class="text-xs tabular-nums text-n-slate-10">{{
                t('SUBSCRIPTION.INVOICES.PAGE', {
                  page: invoices.page,
                  pages: totalInvoicePages,
                })
              }}</span>
            </div>

            <div
              v-if="invoices.records.length"
              class="hidden overflow-hidden rounded-md border border-n-weak sm:block"
            >
              <table class="w-full table-fixed text-start text-sm">
                <thead class="bg-n-alpha-2 text-xs text-n-slate-10">
                  <tr>
                    <th class="w-[23%] px-4 py-3 font-medium">
                      {{ t('SUBSCRIPTION.INVOICES.ID') }}
                    </th>
                    <th class="w-[15%] px-4 py-3 font-medium">
                      {{ t('SUBSCRIPTION.INVOICES.STATUS') }}
                    </th>
                    <th class="w-[17%] px-4 py-3 font-medium">
                      {{ t('SUBSCRIPTION.INVOICES.TOTAL') }}
                    </th>
                    <th class="w-1/5 px-4 py-3 font-medium">
                      {{ t('SUBSCRIPTION.INVOICES.DATE') }}
                    </th>
                    <th class="w-1/4 px-4 py-3 font-medium">
                      {{ t('SUBSCRIPTION.INVOICES.ACTIONS') }}
                    </th>
                  </tr>
                </thead>
                <tbody class="divide-y divide-n-weak text-n-slate-12">
                  <tr v-for="invoice in invoices.records" :key="invoice.id">
                    <td
                      class="px-4 py-3 text-xs"
                      :title="invoice.id"
                    >
                      <span class="block truncate font-mono">{{ invoice.id }}</span>
                      <span
                        v-if="invoicePlanChangeLabel(invoice)"
                        class="mt-1 block text-xs"
                        :class="invoicePlanChangeClass(invoice)"
                      >
                        {{ invoicePlanChangeLabel(invoice) }}
                      </span>
                    </td>
                    <td class="px-4 py-3">
                      {{ translatedStatus(invoice.status) }}
                    </td>
                    <td class="px-4 py-3 tabular-nums">
                      {{ formatMoney(invoice.total_cents, invoice.currency) }}
                    </td>
                    <td class="px-4 py-3">
                      {{
                        invoice.paid_at
                          ? formatDate(invoice.paid_at)
                          : formatDate(invoice.due_at)
                      }}
                    </td>
                    <td class="px-4 py-3">
                      <div
                        v-if="invoicePaymentActionAvailable(invoice)"
                        class="flex flex-wrap gap-2"
                      >
                        <button
                          type="button"
                          class="inline-flex min-h-9 items-center gap-2 rounded-md border border-n-weak px-3 text-xs font-medium text-n-slate-12 disabled:opacity-60"
                          :disabled="Boolean(mutationLoading)"
                          @click="
                            verifyPaymentStatus({ invoiceId: invoice.id })
                          "
                        >
                          <span
                            class="size-4"
                            :class="
                              mutationLoading === 'sync_payment'
                                ? 'i-lucide-loader-circle animate-spin'
                                : 'i-lucide-refresh-cw'
                            "
                          />
                          {{ t('SUBSCRIPTION.PAYMENT.CHECK_STATUS') }}
                        </button>
                        <button
                          type="button"
                          class="inline-flex min-h-9 items-center gap-2 rounded-md border border-n-weak px-3 text-xs font-medium text-n-slate-12 disabled:opacity-60"
                          :disabled="
                            Boolean(mutationLoading) || !summaryIsCurrent
                          "
                          @click="runMutation('renew')"
                        >
                          <span class="i-lucide-external-link size-4" />
                          {{ t('SUBSCRIPTION.PLAN_CHANGE.CONTINUE_PAYMENT') }}
                        </button>
                      </div>
                      <span v-else class="sr-only">
                        {{ t('SUBSCRIPTION.INVOICES.NO_ACTIONS') }}
                      </span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div
              v-if="invoices.records.length"
              class="divide-y divide-n-weak border-y border-n-weak sm:hidden"
            >
              <article
                v-for="invoice in invoices.records"
                :key="invoice.id"
                class="py-4"
              >
                <div class="flex items-start justify-between gap-3">
                  <span class="text-sm font-medium text-n-slate-12">{{
                    translatedStatus(invoice.status)
                  }}</span>
                  <span
                    class="text-sm font-semibold tabular-nums text-n-slate-12"
                    >{{
                      formatMoney(invoice.total_cents, invoice.currency)
                    }}</span
                  >
                </div>
                <p class="mt-2 truncate font-mono text-xs text-n-slate-10">
                  {{ invoice.id }}
                </p>
                <p
                  v-if="invoicePlanChangeLabel(invoice)"
                  class="mt-1 text-xs"
                  :class="invoicePlanChangeClass(invoice)"
                >
                  {{ invoicePlanChangeLabel(invoice) }}
                </p>
                <p class="mt-1 text-xs text-n-slate-10">
                  {{
                    invoice.paid_at
                      ? formatDate(invoice.paid_at)
                      : formatDate(invoice.due_at)
                  }}
                </p>
                <div
                  v-if="invoicePaymentActionAvailable(invoice)"
                  class="mt-3 flex flex-wrap gap-2"
                >
                  <button
                    type="button"
                    class="inline-flex min-h-10 items-center gap-2 rounded-md border border-n-weak px-3 text-sm font-medium text-n-slate-12 disabled:opacity-60"
                    :disabled="Boolean(mutationLoading)"
                    @click="verifyPaymentStatus({ invoiceId: invoice.id })"
                  >
                    <span
                      class="size-4"
                      :class="
                        mutationLoading === 'sync_payment'
                          ? 'i-lucide-loader-circle animate-spin'
                          : 'i-lucide-refresh-cw'
                      "
                    />
                    {{ t('SUBSCRIPTION.PAYMENT.CHECK_STATUS') }}
                  </button>
                  <button
                    type="button"
                    class="inline-flex min-h-10 items-center gap-2 rounded-md border border-n-weak px-3 text-sm font-medium text-n-slate-12 disabled:opacity-60"
                    :disabled="Boolean(mutationLoading) || !summaryIsCurrent"
                    @click="runMutation('renew')"
                  >
                    <span class="i-lucide-external-link size-4" />
                    {{ t('SUBSCRIPTION.PLAN_CHANGE.CONTINUE_PAYMENT') }}
                  </button>
                </div>
              </article>
            </div>

            <p
              v-if="!invoices.records.length"
              class="border-y border-n-weak py-8 text-center text-sm text-n-slate-10"
            >
              {{ t('SUBSCRIPTION.INVOICES.EMPTY') }}
            </p>

            <nav
              v-if="invoices.total > invoices.page_size"
              class="mt-4 flex items-center justify-between gap-3"
              :aria-label="t('SUBSCRIPTION.INVOICES.PAGINATION_LABEL')"
            >
              <button
                type="button"
                class="inline-flex min-h-10 items-center gap-2 rounded-md border border-n-weak px-3 text-sm font-medium text-n-slate-12 disabled:opacity-40"
                :disabled="invoices.page <= 1"
                @click="selectInvoicePage(invoices.page - 1)"
              >
                <span class="i-lucide-chevron-left size-4 rtl:rotate-180" />
                {{ t('SUBSCRIPTION.INVOICES.PREVIOUS') }}
              </button>
              <button
                type="button"
                class="inline-flex min-h-10 items-center gap-2 rounded-md border border-n-weak px-3 text-sm font-medium text-n-slate-12 disabled:opacity-40"
                :disabled="invoices.page >= totalInvoicePages"
                @click="selectInvoicePage(invoices.page + 1)"
              >
                {{ t('SUBSCRIPTION.INVOICES.NEXT') }}
                <span class="i-lucide-chevron-right size-4 rtl:rotate-180" />
              </button>
            </nav>
          </section>
        </template>
      </div>
    </template>
  </SettingsLayout>
</template>
